import { Field } from '../types';
import { isoToDatetimeLocal, toTimeInput, secondsToInterval } from './timestampUtils';

/**
 * Type guard to check if a field is a subform field (one-to-many)
 */
export function isSubformField(field: Field): boolean {
    return field.type === 'one-to-many';
}

/**
 * Cleans subform data by removing foreign keys, timestamps, and other fields
 * that are not part of the form field definition.
 * Keeps only the 'id' field and fields defined in the fields array
 * Converts timestamp fields from ISO to datetime-local format for form inputs
 */
export function cleanSubformData(
    subformData: any[],
    relatedFormFields: Field[]
): any[] {
    if (!Array.isArray(subformData)) {
        return [];
    }

    return subformData.map(item => {
        const cleaned: any = {};

        if (item.id !== undefined && item.id !== null) {
            cleaned.id = item.id;
        }

        relatedFormFields.forEach(field => {
            if (item[field.code] !== undefined) {
                // Convert timestamp from ISO to datetime-local format
                if (field.type === 'timestamp') {
                    cleaned[field.code] = isoToDatetimeLocal(item[field.code]);
                } else if (field.type === 'time') {
                    // Convert time to HH:mm format
                    cleaned[field.code] = toTimeInput(item[field.code]);
                } else if (field.type === 'interval') {
                    // Convert duration (seconds) to HH:MM
                    cleaned[field.code] = secondsToInterval(item[field.code]);
                } else {
                    cleaned[field.code] = item[field.code];
                }
            }
        });

        return cleaned;
    });
}

/**
 * Creates an empty subform item with default values based on field types
 */
export function createEmptySubformItem(fields: Field[]): Record<string, any> {
    const emptyItem: Record<string, any> = {};

    fields.forEach(field => {
        switch (field.type) {
            case 'integer':
            case 'float':
            case 'number':
                emptyItem[field.code] = '';
                break;
            case 'boolean':
                emptyItem[field.code] = false;
                break;
            default:
                emptyItem[field.code] = '';
        }
    });

    return emptyItem;
}

/**
 * Sets form errors for both top-level fields and nested subform fields
 * Handles the nested error structure from the API
 */
export function setFormErrors(errors: any, setFieldError: (field: string, message: string) => void) {
    if (!errors || typeof errors !== 'object') {
        return;
    }

    Object.entries(errors).forEach(([fieldCode, error]) => {
        if (typeof error === 'string') {
            setFieldError(fieldCode, error);
        } else if (Array.isArray(error)) {
            setFieldError(fieldCode, error[0] || 'Error');
        } else if (typeof error === 'object' && error !== null) {
            Object.entries(error).forEach(([index, itemErrors]) => {
                if (typeof itemErrors === 'object' && itemErrors !== null) {
                    Object.entries(itemErrors).forEach(([itemField, itemError]) => {
                        const errorMessage = Array.isArray(itemError) ? itemError[0] : String(itemError);
                        setFieldError(`${fieldCode}[${index}].${itemField}`, errorMessage);
                    });
                }
            });
        }
    });
}
